import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface SparklinePoint {
  value: number;
}

export interface TrendResult {
  currentValue: number;
  previousValue: number;
  percentChange: number;
  isPositive: boolean;
  label: string;
  sparklineData?: SparklinePoint[];
}

interface TrendOptions {
  agencyId?: string;
  period?: 'week' | 'month' | 'year';
}

// Calculate percentage change between two values
export function calculateTrend(current: number, previous: number): { percentChange: number; isPositive: boolean } {
  if (previous === 0) {
    return { percentChange: current > 0 ? 100 : 0, isPositive: current > 0 };
  }
  const change = ((current - previous) / previous) * 100;
  return { 
    percentChange: Math.round(Math.abs(change)), 
    isPositive: change >= 0 
  };
}

// Hook to calculate trends for dashboard stats
export function useDashboardTrends(options: TrendOptions = {}) {
  const { agencyId, period = 'week' } = options;
  const [trends, setTrends] = useState<{
    patients: TrendResult | null;
    sessions: TrendResult | null;
    redFlags: TrendResult | null;
    recentActivity: TrendResult | null;
  }>({
    patients: null,
    sessions: null,
    redFlags: null,
    recentActivity: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTrends = async () => {
      try {
        const now = new Date();
        const periodMs = period === 'week' 
          ? 7 * 24 * 60 * 60 * 1000 
          : period === 'month' 
            ? 30 * 24 * 60 * 60 * 1000 
            : 365 * 24 * 60 * 60 * 1000;
        const currentStart = new Date(now.getTime() - periodMs);
        const previousStart = new Date(currentStart.getTime() - periodMs);
        const periodLabel = period === 'week' 
          ? 'vs last week' 
          : period === 'month' 
            ? 'vs last month' 
            : 'vs last year';

        // Calculate number of segments for sparkline (6-8 points)
        const segmentCount = period === 'week' ? 7 : period === 'month' ? 8 : 12;
        const segmentMs = periodMs / segmentCount;

        // Build base queries
        const patientQuery = agencyId 
          ? supabase.from('patients').select('created_at').eq('agency_id', agencyId)
          : supabase.from('patients').select('created_at');
        
        const sessionQuery = agencyId
          ? supabase.from('triage_sessions').select('created_at, red_flags').eq('agency_id', agencyId)
          : supabase.from('triage_sessions').select('created_at, red_flags');

        // Fetch all data
        const [patientsResult, sessionsResult] = await Promise.all([
          patientQuery,
          sessionQuery
        ]);

        const patients = patientsResult.data || [];
        const sessions = sessionsResult.data || [];

        // Helper function to generate sparkline data
        const generateSparkline = (items: { created_at: string }[], filterFn?: (item: any) => boolean) => {
          const sparkline: SparklinePoint[] = [];
          for (let i = 0; i < segmentCount; i++) {
            const segStart = new Date(currentStart.getTime() + i * segmentMs);
            const segEnd = new Date(segStart.getTime() + segmentMs);
            const count = items.filter(item => {
              const date = new Date(item.created_at);
              const inRange = date >= segStart && date < segEnd;
              return filterFn ? inRange && filterFn(item) : inRange;
            }).length;
            sparkline.push({ value: count });
          }
          return sparkline;
        };

        // Calculate patient trends
        const currentPatients = patients.filter(p => new Date(p.created_at) >= currentStart).length;
        const previousPatients = patients.filter(p => 
          new Date(p.created_at) >= previousStart && new Date(p.created_at) < currentStart
        ).length;
        const patientTrend = calculateTrend(currentPatients, previousPatients);
        const patientSparkline = generateSparkline(patients);

        // Calculate session trends
        const currentSessions = sessions.filter(s => new Date(s.created_at) >= currentStart).length;
        const previousSessions = sessions.filter(s => 
          new Date(s.created_at) >= previousStart && new Date(s.created_at) < currentStart
        ).length;
        const sessionTrend = calculateTrend(currentSessions, previousSessions);
        const sessionSparkline = generateSparkline(sessions);

        // Calculate red flag trends
        const currentRedFlags = sessions.filter(s => 
          s.red_flags && new Date(s.created_at) >= currentStart
        ).length;
        const previousRedFlags = sessions.filter(s => 
          s.red_flags && new Date(s.created_at) >= previousStart && new Date(s.created_at) < currentStart
        ).length;
        const redFlagTrend = calculateTrend(currentRedFlags, previousRedFlags);
        const redFlagSparkline = generateSparkline(sessions, (s) => s.red_flags);

        // For recent activity, compare this period to the previous period
        const recentActivityTrend = calculateTrend(currentSessions, previousSessions);

        setTrends({
          patients: {
            currentValue: currentPatients,
            previousValue: previousPatients,
            ...patientTrend,
            label: periodLabel,
            sparklineData: patientSparkline
          },
          sessions: {
            currentValue: currentSessions,
            previousValue: previousSessions,
            ...sessionTrend,
            label: periodLabel,
            sparklineData: sessionSparkline
          },
          redFlags: {
            currentValue: currentRedFlags,
            previousValue: previousRedFlags,
            // For red flags, decrease is positive (less emergencies is good)
            percentChange: redFlagTrend.percentChange,
            isPositive: !redFlagTrend.isPositive || redFlagTrend.percentChange === 0,
            label: periodLabel,
            sparklineData: redFlagSparkline
          },
          recentActivity: {
            currentValue: currentSessions,
            previousValue: previousSessions,
            ...recentActivityTrend,
            label: periodLabel,
            sparklineData: sessionSparkline
          }
        });
      } catch (error) {
        console.error('Error calculating trends:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTrends();
  }, [agencyId, period]);

  return { trends, loading };
}

// Simplified trend for a single metric
export function useSingleTrend(
  table: string,
  column: string = 'created_at',
  filter?: { column: string; value: any },
  period: 'week' | 'month' | 'year' = 'week'
) {
  const [trend, setTrend] = useState<TrendResult | null>(null);

  useEffect(() => {
    const fetchTrend = async () => {
      const now = new Date();
      const periodMs = period === 'week' 
        ? 7 * 24 * 60 * 60 * 1000 
        : period === 'month' 
          ? 30 * 24 * 60 * 60 * 1000 
          : 365 * 24 * 60 * 60 * 1000;
      const currentStart = new Date(now.getTime() - periodMs);
      const previousStart = new Date(currentStart.getTime() - periodMs);
      const periodLabel = period === 'week' 
        ? 'vs last week' 
        : period === 'month' 
          ? 'vs last month' 
          : 'vs last year';

      try {
        let query = supabase.from(table as any).select(column);
        if (filter) {
          query = query.eq(filter.column, filter.value);
        }

        const { data } = await query;
        const items = data || [];

        const currentCount = items.filter((item: any) => new Date(item[column]) >= currentStart).length;
        const previousCount = items.filter((item: any) => 
          new Date(item[column]) >= previousStart && new Date(item[column]) < currentStart
        ).length;

        const trendCalc = calculateTrend(currentCount, previousCount);
        setTrend({
          currentValue: currentCount,
          previousValue: previousCount,
          ...trendCalc,
          label: periodLabel
        });
      } catch (error) {
        console.error('Error calculating single trend:', error);
      }
    };

    fetchTrend();
  }, [table, column, filter?.column, filter?.value, period]);

  return trend;
}
